import { constructAccessToken, querystring } from '@/app/utils/apiCommonUtils';
import { countryConfig } from '@/app/utils/countryConfig';
import { captureException } from '@sentry/nextjs';

export const isClientFetchError = (error: unknown): error is ClientFetchError => {
  return (error as any)?.name === 'ClientFetchError';
};

class ClientFetchError extends Error {
  public status: number;
  public url: string;
  public body?: unknown; // parsed JSON response body (if any)
  public rawText: string;

  constructor(message: string, status: number, url: string, rawText: string, parsedBody?: unknown) {
    super(message);
    this.name = 'ClientFetchError';
    this.status = status;
    this.url = url;
    this.rawText = rawText;
    this.body = parsedBody;
  }
}

export const clientAuthFetcher = async <TData, TVariables = unknown>({
  url,
  method,
  body,
  data,
  params,
  headers,
  signal,
  responseType,
}: {
  url: string;
  method: string;
  body?: TVariables;
  data?: TVariables;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  responseType?: 'json' | 'blob';
}): Promise<TData> => {
  const payload = body ?? data;
  const query = params ? `?${querystring(params)}` : '';

  const location = window.location.search;

  const isEnglish = location.includes('lang=en');

  const res = await fetch(`${url}${query}`, {
    method,
    headers: {
      ...headers,
      'Content-Type': 'application/json',
      Authorization: constructAccessToken(process.env.NEXT_PUBLIC_API_TOKEN ?? ''),
      Accept: 'application/json',
      'X-Auth-Token': 'bypass',
      credentials: 'include',
      'accept-language': isEnglish ? 'en' : countryConfig.fallbackLanguage, // Use i18n instance to get the current language
    },
    body: payload ? JSON.stringify(payload) : undefined,
    signal,
  });

  if (!res.ok) {
    const errorText = await res.text();

    let parsedBody: unknown;
    try {
      parsedBody = JSON.parse(errorText);
    } catch {
      parsedBody = undefined;
    }

    const clientError = new ClientFetchError(
      `HTTP ${res.status} ${res.statusText}`,
      res.status,
      `${url}${query}`,
      errorText,
      parsedBody,
    );

    captureException(clientError, {
      extra: {
        url: `${url}${query}`,
        method,
        payload,
        status: res.status,
        responseText: errorText,
        parsedBody,
      },
    });

    throw clientError;
  }

  if (responseType === 'blob') {
    return res.blob() as unknown as TData;
  }

  const jsonData = res.status === 200 ? await res.json() : undefined;

  return jsonData;
};
